// src/lib/data/sync/DeduplicationGuard.js - V1 ENTERPRISE ULTRA-ROBUST
// ═══════════════════════════════════════════════════════════════════════════
// 🛡️ DEDUPLICATION GUARD — Google Drive / Dropbox / Notion Grade
// ═══════════════════════════════════════════════════════════════════════════
// Propósito: Prevención ABSOLUTA de duplicados en cualquier capa del sistema
//
// 🔥 V1: FINGERPRINT HASH — SHA256-like hash por contenido (no solo título)
// 🔥 V1: MULTI-CAMPO — Properties: title+price+city+type / Clients: email+phone+name
// 🔥 V1: INTERCEPTOR CENTRAL — Todas las escrituras pasan por aquí
// 🔥 V1: MERGE INTELIGENTE — Si hay duplicado, fusiona en vez de crear nuevo
// 🔥 V1: CLEANUP EXISTENTE — Elimina duplicados pre-existentes en una pasada
// 🔥 V1: BATCH PROTECTION — Limpia lotes antes de guardar en IndexedDB
// 🔥 V1: STATS — Métricas de duplicados detectados y prevenidos
// 🔥 V1: ZERO DEPENDENCIAS — Solo usa APIs estándar del navegador
// 🔥 V1: ASÍNCRONO — No bloquea el hilo principal
// ═══════════════════════════════════════════════════════════════════════════

// ═══════════════════════════════════════════════════════════════════════════
// 📐 CONFIGURACIÓN
// ═══════════════════════════════════════════════════════════════════════════

const CONFIG = {
  // Campos usados para generar fingerprint por tipo de colección
  FINGERPRINT_FIELDS: {
    properties: ['title', 'price', 'city', 'neighborhood', 'type', 'bedrooms', 'area'],
    clients: ['email', 'name', 'phone', 'cpf'],
    tasks: ['title', 'clientId', 'propertyId', 'dueDate'],
    calendarEvents: ['title', 'propertyId', 'date', 'clientId'],
    documents: ['name', 'propertyId', 'clientId', 'type'],
    financeiro: ['description', 'type', 'value', 'date', 'propertyId', 'clientId'],
    messages: ['clientId', 'propertyId', 'type'],
    interactions: ['clientId', 'type', 'date'],
    users: ['email', 'name'],
    team_members: ['email', 'companyId', 'userId'],
    team_invites: ['email', 'companyId', 'token'],
    // Default: usa id si está disponible, sino title/name
    default: ['id', 'title', 'name'],
  },
  
  // Umbral de similitud para considerar duplicado (0-1, 1 = exacto)
  SIMILARITY_THRESHOLD: 0.85,
  
  // Máximo de documentos a procesar en una limpieza
  MAX_CLEANUP_BATCH: 500,
  
  // Intervalo de limpieza automática (en operaciones)
  AUTO_CLEANUP_INTERVAL: 100,
};

// ═══════════════════════════════════════════════════════════════════════════
// 🔐 GENERADOR DE FINGERPRINT (Hash rápido sin librerías)
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Genera un hash simple pero efectivo para detectar duplicados.
 * No es criptográfico, está optimizado para velocidad.
 * 
 * Algoritmo: djb2 modificado con seed aleatorio por sesión.
 */
let _hashSeed = Date.now() % 100000;

function _quickHash(str) {
  if (!str) return '0';
  
  let h = 5381 ^ _hashSeed;
  for (let i = 0; i < str.length; i++) {
    h = ((h << 5) + h) ^ str.charCodeAt(i);
    h = h >>> 0; // Convertir a unsigned 32-bit
  }
  return h.toString(36);
}

/**
 * Genera un fingerprint normalizado para un documento.
 * 
 * Para properties:
 *   "casa playa_100000_miami_casa_3_150" (title_price_city_type_bedrooms_area)
 * 
 * Para clients:
 *   "juan@email.com" o "juan perez_+5491112345678" (email o name_phone)
 */
function generateFingerprint(collection, data) {
  if (!collection || !data || typeof data !== 'object') return null;
  
  const fields = CONFIG.FINGERPRINT_FIELDS[collection] || CONFIG.FINGERPRINT_FIELDS.default;
  
  // Construir string normalizado con campos relevantes
  const parts = [];
  
  for (const field of fields) {
    let val = data[field];
    
    // Ignorar campos vacíos
    if (val === undefined || val === null || val === '') continue;
    
    // Normalizar: minúsculas, sin espacios extra, sin símbolos
    if (typeof val === 'string') {
      val = val.toLowerCase()
        .trim()
        .replace(/\s+/g, ' ')
        .replace(/[^\w\s@.+]/g, '')
        .replace(/\s/g, '_');
    }
    
    // Formatear números sin decimales innecesarios
    if (typeof val === 'number') {
      val = Number.isInteger(val) ? val.toString() : val.toFixed(0);
    }
    
    if (val !== '' && val !== '0' && val !== 'undefined') {
      parts.push(String(val));
    }
  }
  
  // Si no hay suficientes campos, usar ID como fallback
  if (parts.length === 0 && data.id) {
    parts.push(String(data.id));
  }
  
  // Si no hay nada, no se puede generar fingerprint
  if (parts.length === 0) return null;
  
  const raw = parts.join('_').replace(/_{2,}/g, '_').replace(/^_|_$/g, '');
  
  return {
    collection,
    raw,
    hash: _quickHash(raw),
    fields: parts.length,
  };
}

/**
 * Compara dos fingerprints para determinar si son el mismo documento.
 * Retorna un score de similitud (0-1).
 */
function compareFingerprints(fp1, fp2) {
  if (!fp1 || !fp2) return 0;
  if (fp1.hash === fp2.hash) return 1;
  if (fp1.collection !== fp2.collection) return 0;
  
  // Comparar campos individuales
  const parts1 = fp1.raw.split('_');
  const parts2 = fp2.raw.split('_');
  
  let matches = 0;
  const maxLen = Math.max(parts1.length, parts2.length);
  
  for (const p1 of parts1) {
    if (parts2.includes(p1)) matches++;
  }
  
  return maxLen > 0 ? matches / maxLen : 0;
}

// ═══════════════════════════════════════════════════════════════════════════
// 🛡️ DEDUPLICATION GUARD — CLASE PRINCIPAL
// ═══════════════════════════════════════════════════════════════════════════

class DeduplicationGuard {
  /**
   * @param {Object} deps - Dependencias
   * @param {Object} deps.local - LocalStrategy instance
   * @param {Object} deps.cloud - CloudStrategy instance (opcional)
   * @param {string} deps.userId - ID del usuario
   * @param {string} deps.companyId - ID de la compañía
   */
  constructor(deps = {}) {
    this.local = deps.local || null;
    this.cloud = deps.cloud || null;
    this.userId = deps.userId || 'unknown';
    this.companyId = deps.companyId || 'unknown';
    
    // Caché de fingerprints en memoria (válida por sesión)
    this._fingerprintCache = new Map();
    
    // Métricas
    this._metrics = {
      totalChecks: 0,
      duplicatesFound: 0,
      duplicatesPrevented: 0,
      mergesPerformed: 0,
      cleanupsPerformed: 0,
      documentsCleaned: 0,
      byCollection: {},
    };
    
    // Contador de operaciones para limpieza automática
    this._operationCount = 0;
    
    console.log(
      `🛡️ DeduplicationGuard V1 — User: ${this.userId} | Company: ${this.companyId} | ` +
      `Local: ${!!this.local} | Cloud: ${!!this.cloud}`
    );
  }

  // ═══════════════════════════════════════════════════════════════════════
  // 🔍 DETECCIÓN DE DUPLICADOS
  // ═══════════════════════════════════════════════════════════════════════

  /**
   * Verifica si un documento ya existe en el almacenamiento local.
   * 
   * @param {string} collection - Nombre de la colección
   * @param {Object} data - Datos del documento a verificar
   * @returns {Object|null} El documento existente si hay duplicado, null si no
   */
  async findDuplicate(collection, data) {
    if (!this.local || !collection || !data) return null;
    
    const fingerprint = generateFingerprint(collection, data);
    if (!fingerprint) return null;
    
    this._metrics.totalChecks++;
    
    try {
      // Obtener todos los documentos de la colección
      const items = await this.local.read(collection, {}, {});
      if (!Array.isArray(items) || items.length === 0) return null;
      
      // Buscar por fingerprint
      for (const item of items) {
        // Mismo ID → es el mismo documento, no duplicado
        if (data.id && String(item.id) === String(data.id)) continue;
        if (data._localId && String(item._localId) === String(data._localId)) continue;
        
        const itemFingerprint = generateFingerprint(collection, item);
        if (!itemFingerprint) continue;
        
        const similarity = compareFingerprints(fingerprint, itemFingerprint);
        
        if (similarity >= CONFIG.SIMILARITY_THRESHOLD) {
          this._metrics.duplicatesFound++;
          
          if (!this._metrics.byCollection[collection]) {
            this._metrics.byCollection[collection] = { found: 0, prevented: 0 };
          }
          this._metrics.byCollection[collection].found++;
          
          return item; // Retorna el documento existente
        }
      }
      
      return null;
    } catch (error) {
      console.warn(`⚠️ [DeduplicationGuard] Error buscando duplicado en ${collection}:`, error.message);
      return null;
    }
  }

  /**
   * Verifica si un documento ya existe (por ID exacto, más rápido)
   */
  async findById(collection, id) {
    if (!this.local || !collection || !id) return null;
    
    try {
      const items = await this.local.read(collection, {}, {});
      if (!Array.isArray(items)) return null;
      
      const strId = String(id);
      return items.find(item => 
        String(item.id) === strId || 
        String(item._localId) === strId || 
        String(item._firestoreId) === strId
      ) || null;
    } catch (error) {
      return null;
    }
  }

  // ═══════════════════════════════════════════════════════════════════════
  // 🛡️ INTERCEPTOR DE ESCRITURA
  // ═══════════════════════════════════════════════════════════════════════

  /**
   * Intercepta una creación para prevenir duplicados.
   * Si el documento ya existe, hace merge en vez de crear uno nuevo.
   * 
   * @param {string} collection - Nombre de la colección
   * @param {Object} data - Datos a crear
   * @returns {Object} { created: false, merged: true, existingId, result }
   */
  async interceptCreate(collection, data) {
    if (!this.local || !collection || !data) {
      return { intercepted: false, reason: 'invalid_params' };
    }
    
    this._operationCount++;
    
    // Verificar si ya existe por ID
    if (data.id) {
      const existingById = await this.findById(collection, data.id);
      if (existingById) {
        this._metrics.duplicatesPrevented++;
        this._incrementCollectionMetric(collection, 'prevented');
        
        console.log(`🛡️ [DeduplicationGuard] CREATE bloqueado: ${collection}/${data.id} ya existe por ID`);
        
        // Merge: actualizar el existente con los nuevos datos
        const merged = { ...existingById, ...data, updatedAt: new Date().toISOString() };
        const result = await this.local.update(collection, existingById.id, merged);
        
        this._metrics.mergesPerformed++;
        
        return {
          intercepted: true,
          reason: 'duplicate_id',
          existingId: existingById.id,
          merged: true,
          result,
        };
      }
    }
    
    // Verificar por contenido (fingerprint)
    const duplicate = await this.findDuplicate(collection, data);
    if (duplicate) {
      this._metrics.duplicatesPrevented++;
      this._incrementCollectionMetric(collection, 'prevented');
      
      console.log(`🛡️ [DeduplicationGuard] CREATE bloqueado: duplicado de "${duplicate.title || duplicate.name || duplicate.id}" en ${collection}`);
      
      // Merge inteligente: conservar la versión más completa
      const merged = this._mergeDocuments(duplicate, data, collection);
      const result = await this.local.update(collection, duplicate.id, merged);
      
      this._metrics.mergesPerformed++;
      
      return {
        intercepted: true,
        reason: 'duplicate_content',
        existingId: duplicate.id,
        fingerprint: generateFingerprint(collection, data),
        merged: true,
        result,
      };
    }
    
    // No hay duplicado, permitir creación
    return { intercepted: false };
  }

  /**
   * Intercepta un lote de documentos para eliminar duplicados internos
   * y duplicados contra documentos existentes.
   * 
   * @param {string} collection - Nombre de la colección
   * @param {Array} documents - Array de documentos
   * @returns {Array} Documentos sin duplicados
   */
  async interceptBatch(collection, documents) {
    if (!Array.isArray(documents) || documents.length === 0) return documents;
    
    const seen = new Set();
    const clean = [];
    let removed = 0;
    
    for (const doc of documents) {
      const fingerprint = generateFingerprint(collection, doc);
      
      // Verificar duplicados dentro del mismo lote
      if (fingerprint) {
        if (seen.has(fingerprint.hash)) {
          removed++;
          console.log(`🛡️ [DeduplicationGuard] BATCH: eliminado duplicado interno en ${collection}`);
          continue;
        }
        seen.add(fingerprint.hash);
      }
      
      // Verificar duplicados contra existentes
      if (doc.id) {
        const existingById = await this.findById(collection, doc.id);
        if (existingById) {
          removed++;
          console.log(`🛡️ [DeduplicationGuard] BATCH: ${collection}/${doc.id} ya existe, omitiendo`);
          continue;
        }
      }
      
      const duplicate = await this.findDuplicate(collection, doc);
      if (duplicate) {
        removed++;
        console.log(`🛡️ [DeduplicationGuard] BATCH: duplicado detectado en ${collection}, omitiendo`);
        continue;
      }
      
      clean.push(doc);
    }
    
    if (removed > 0) {
      this._metrics.duplicatesPrevented += removed;
      console.log(`🛡️ [DeduplicationGuard] BATCH: ${removed} duplicados prevenidos en ${collection}`);
    }
    
    return clean;
  }

  // ═══════════════════════════════════════════════════════════════════════
  // 🔄 MERGE INTELIGENTE
  // ═══════════════════════════════════════════════════════════════════════

  /**
   * Fusiona dos documentos duplicados, conservando la mejor versión.
   * 
   * Reglas:
   * - Arrays (images, tags) → concatenar y deduplicar
   * - Strings largos (description) → conservar el más largo
   * - Fechas → conservar la más reciente
   * - Campos con valor vs vacío → conservar el que tiene valor
   */
  _mergeDocuments(existing, incoming, collection) {
    const merged = { ...existing };
    
    // Campos que deben concatenarse (arrays)
    const arrayFields = ['images', 'tags', 'features', 'linkedClients', 'propertyIds'];
    
    // Campos que deben conservar el valor más largo (descripciones)
    const longTextField = ['description', 'notes', 'observations'];
    
    for (const key of Object.keys(incoming)) {
      // No sobrescribir ID
      if (key === 'id' || key === '_localId' || key === '_firestoreId') continue;
      
      const existingVal = existing[key];
      const incomingVal = incoming[key];
      
      // Si el existente no tiene valor, usar el incoming
      if (existingVal === undefined || existingVal === null || existingVal === '') {
        merged[key] = incomingVal;
        continue;
      }
      
      // Si el incoming no tiene valor, conservar existente
      if (incomingVal === undefined || incomingVal === null || incomingVal === '') {
        continue;
      }
      
      // Arrays → concatenar sin duplicados
      if (arrayFields.includes(key) && Array.isArray(existingVal) && Array.isArray(incomingVal)) {
        const combined = [...existingVal];
        for (const item of incomingVal) {
          if (!combined.some(e => JSON.stringify(e) === JSON.stringify(item))) {
            combined.push(item);
          }
        }
        merged[key] = combined;
        continue;
      }
      
      // Strings largos → conservar el más largo
      if (longTextField.includes(key) && typeof existingVal === 'string' && typeof incomingVal === 'string') {
        merged[key] = incomingVal.length > existingVal.length ? incomingVal : existingVal;
        continue;
      }
      
      // Fechas → conservar la más reciente
      if (key === 'updatedAt' || key === 'createdAt' || key === '_syncedAt') {
        const existingDate = new Date(existingVal).getTime();
        const incomingDate = new Date(incomingVal).getTime();
        merged[key] = incomingDate > existingDate ? incomingVal : existingVal;
        continue;
      }
      
      // Para todo lo demás, el incoming gana (datos más frescos)
      merged[key] = incomingVal;
    }
    
    // Actualizar metadata
    merged.updatedAt = new Date().toISOString();
    merged._mergedAt = new Date().toISOString();
    merged._mergedFrom = existing.id;
    merged._deduplicated = true;
    
    return merged;
  }

  // ═══════════════════════════════════════════════════════════════════════
  // 🧹 LIMPIEZA DE DUPLICADOS EXISTENTES
  // ═══════════════════════════════════════════════════════════════════════

  /**
   * Limpia todos los duplicados de una colección.
   * Conserva la versión más completa de cada documento.
   * 
   * @param {string} collection - Nombre de la colección
   * @returns {Object} { total, duplicatesFound, removed, kept }
   */
  async cleanCollection(collection) {
    if (!this.local) {
      return { success: false, error: 'local_unavailable' };
    }
    
    const t0 = performance.now();
    
    try {
      const items = await this.local.read(collection, {}, {});
      if (!Array.isArray(items) || items.length <= 1) {
        return { 
          success: true, 
          total: items?.length || 0, 
          duplicatesFound: 0, 
          removed: 0, 
          kept: items?.length || 0,
          elapsed: 0,
        };
      }
      
      console.log(`🧹 [DeduplicationGuard] Limpiando ${collection}: ${items.length} documentos...`);
      
      const fingerprintMap = new Map();
      const toDelete = [];
      const toUpdate = [];
      let kept = 0;
      
      // Primera pasada: agrupar por fingerprint
      for (const item of items) {
        const fp = generateFingerprint(collection, item);
        const key = fp ? fp.hash : item.id;
        
        if (!fingerprintMap.has(key)) {
          fingerprintMap.set(key, []);
        }
        fingerprintMap.get(key).push(item);
      }
      
      // Segunda pasada: resolver duplicados
      for (const [key, duplicates] of fingerprintMap.entries()) {
        if (duplicates.length === 1) {
          kept++;
          continue;
        }
        
        // Ordenar por "completitud": más imágenes, más campos, fecha más reciente
        duplicates.sort((a, b) => {
          const scoreA = this._completenessScore(a, collection);
          const scoreB = this._completenessScore(b, collection);
          return scoreB - scoreA;
        });
        
        // Conservar el mejor
        const best = duplicates[0];
        kept++;
        
        // Mergear datos de los demás en el mejor
        for (let i = 1; i < duplicates.length; i++) {
          const merged = this._mergeDocuments(best, duplicates[i], collection);
          toUpdate.push({ id: best.id, data: merged });
          toDelete.push(duplicates[i].id);
        }
      }
      
      // Ejecutar eliminaciones
      let removed = 0;
      for (const id of toDelete) {
        try {
          await this.local.delete(collection, id, { silent: true });
          removed++;
        } catch (e) {
          console.warn(`⚠️ [DeduplicationGuard] Error eliminando ${collection}/${id}:`, e.message);
        }
      }
      
      // Ejecutar actualizaciones (merge)
      for (const { id, data } of toUpdate) {
        try {
          await this.local.update(collection, id, data);
        } catch (e) {
          console.warn(`⚠️ [DeduplicationGuard] Error actualizando ${collection}/${id}:`, e.message);
        }
      }
      
      const elapsed = performance.now() - t0;
      
      // Invalidar caché
      if (typeof this.local.invalidateCache === 'function') {
        this.local.invalidateCache(collection);
      }
      
      this._metrics.cleanupsPerformed++;
      this._metrics.documentsCleaned += removed;
      
      console.log(
        `✅ [DeduplicationGuard] ${collection} limpio: ${removed} eliminados, ${kept} conservados | ` +
        `${Math.round(elapsed)}ms`
      );
      
      return {
        success: true,
        total: items.length,
        duplicatesFound: toDelete.length,
        removed,
        kept,
        elapsed,
      };
    } catch (error) {
      console.error(`❌ [DeduplicationGuard] Error limpiando ${collection}:`, error.message);
      return {
        success: false,
        error: error.message,
        elapsed: performance.now() - t0,
      };
    }
  }

  /**
   * Limpia todas las colecciones
   */
  async cleanAll(collections = null) {
    const cols = collections || Object.keys(CONFIG.FINGERPRINT_FIELDS).filter(k => k !== 'default');
    const results = {};
    let totalRemoved = 0;
    
    console.log(`🧹 [DeduplicationGuard] Iniciando limpieza de ${cols.length} colecciones...`);
    
    for (const col of cols) {
      results[col] = await this.cleanCollection(col);
      totalRemoved += results[col].removed || 0;
    }
    
    console.log(`✅ [DeduplicationGuard] Limpieza completa: ${totalRemoved} duplicados eliminados en total`);
    
    return {
      success: true,
      collections: results,
      totalRemoved,
    };
  }

  /**
   * Calcula un score de "completitud" para un documento.
   * Más imágenes, más campos con valor = mayor score.
   */
  _completenessScore(item, collection) {
    if (!item) return 0;
    
    let score = 0;
    
    // Puntos por campos con valor
    const fields = CONFIG.FINGERPRINT_FIELDS[collection] || [];
    for (const field of fields) {
      if (item[field] !== undefined && item[field] !== null && item[field] !== '') {
        score += 1;
      }
    }
    
    // Bonus por imágenes
    if (Array.isArray(item.images)) {
      score += item.images.length * 2;
    }
    
    // Bonus por descripción
    if (item.description && typeof item.description === 'string') {
      score += Math.min(item.description.length / 100, 5);
    }
    
    // Bonus por fecha reciente
    const updatedAt = new Date(item.updatedAt || item.createdAt || 0).getTime();
    if (updatedAt > 0) {
      score += updatedAt / 1e12; // Normalizar a ~1-2 puntos
    }
    
    return score;
  }

  // ═══════════════════════════════════════════════════════════════════════
  // 📊 MÉTRICAS Y ESTADO
  // ═══════════════════════════════════════════════════════════════════════

  _incrementCollectionMetric(collection, field) {
    if (!this._metrics.byCollection[collection]) {
      this._metrics.byCollection[collection] = { found: 0, prevented: 0 };
    }
    this._metrics.byCollection[collection][field]++;
  }

  getMetrics() {
    return {
      ...this._metrics,
      fingerprintCacheSize: this._fingerprintCache.size,
      operationCount: this._operationCount,
      version: 'V1',
    };
  }

  resetMetrics() {
    this._metrics = {
      totalChecks: 0,
      duplicatesFound: 0,
      duplicatesPrevented: 0,
      mergesPerformed: 0,
      cleanupsPerformed: 0,
      documentsCleaned: 0,
      byCollection: {},
    };
    this._operationCount = 0;
    this._fingerprintCache.clear();
  }

  // ═══════════════════════════════════════════════════════════════════════
  // ♻️ DESTRUCCIÓN
  // ═══════════════════════════════════════════════════════════════════════

  destroy() {
    this._fingerprintCache.clear();
    console.log('♻️ DeduplicationGuard V1 destruido');
  }
}

export default DeduplicationGuard;
export { 
  CONFIG as DeduplicationConfig, 
  generateFingerprint, 
  compareFingerprints 
};